import React, { useState, useMemo, useCallback } from 'react';
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    Typography,
    Box,
    Divider,
    CircularProgress
} from '@mui/material';
import { TertiaryButton } from './common/Buttons';
import {
    Assignment as AssignmentIcon,
    Update as UpdateIcon,
    Visibility as VisibilityIcon,
    Refresh as RefreshIcon,
    NotificationsOutlined,
    Inventory as InventoryIcon
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import { getRelatedObjectRoute } from '../utils/notificationRoutes';

const NotificationBell = () => {
    const { notifications, unreadCount, markAsRead, markAllAsRead, refreshNotifications, loading } = useNotifications();
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();


    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleNotificationClick = useCallback(async (notification) => {
        // Mark as read
        if (!notification.is_read) {
            await markAsRead(notification.id);
        }

        // Navigate based on notification type
        if (notification.notification_type === 'low_stock') {
            navigate('/inventory');
            handleClose();
            return;
        }
        const relatedRoute = getRelatedObjectRoute(notification);
        if (relatedRoute) {
            navigate(relatedRoute);
        } else if (notification.related_service_order_id) {
            navigate(`/service-orders/${notification.related_service_order_label || notification.related_service_order_id}`);
        } else {
            toast.info('No relevant data found for this notification');
        }

        handleClose();
    }, [markAsRead, navigate, handleClose]);

    const handleMarkAllAsRead = useCallback(async () => {
        await markAllAsRead();
        handleClose();
    }, [markAllAsRead, handleClose]);

    const handleRefresh = useCallback(async () => {
        await refreshNotifications();
    }, [refreshNotifications]);

    const getNotificationIcon = useCallback((type) => {
        switch (type) {
            case 'assignment':
                return <AssignmentIcon fontSize="small" color="primary" />;
            case 'state_change':
                return <UpdateIcon fontSize="small" color="secondary" />;
            case 'low_stock':
                return <InventoryIcon fontSize="small" sx={{ color: 'warning.main' }} />;
            default:
                return <VisibilityIcon fontSize="small" />;
        }
    }, []);

    const formatTime = useCallback((dateString) => {
        try {
            return formatDistanceToNow(new Date(dateString), { addSuffix: true });
        } catch (error) {
            return 'Unknown time';
        }
    }, []);

    const open = Boolean(anchorEl);

    // Memoize the notifications to prevent unnecessary re-renders
    const displayNotifications = useMemo(() => {
        return notifications.slice(0, 10);
    }, [notifications]);

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleClick}
                size="small"
                sx={{
                    ml: 1,
                    width: 32,
                    height: 32
                }}
            >
                <Badge badgeContent={unreadCount} color="primary" max={999}>
                    <NotificationsOutlined fontSize="small" />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: 420,
                        maxWidth: '95vw',
                        height: 560,
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Header: fixed at top */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="div">
                            Notifications
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleRefresh}
                            disabled={loading}
                            title="Refresh notifications"
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {unreadCount > 0 && (
                        <TertiaryButton
                            size="small"
                            onClick={handleMarkAllAsRead}
                            sx={{ mt: 1 }}
                        >
                            Mark all as read
                        </TertiaryButton>
                    )}
                </Box>

                {/* Scrollable list: takes remaining space and scrolls */}
                <Box
                    sx={{
                        flex: '1 1 0',
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : displayNotifications.length === 0 ? (
                        <MenuItem disabled>
                            <Typography variant="body2" color="text.secondary">
                                No notifications
                            </Typography>
                        </MenuItem>
                    ) : (
                        displayNotifications.map((notification) => (
                            <MenuItem
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    p: 2,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    backgroundColor: notification.is_read ? 'inherit' : 'action.hover',
                                    '&:hover': {
                                        backgroundColor: 'action.selected'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                                    {getNotificationIcon(notification.notification_type)}
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            ml: 1,
                                            flexGrow: 1,
                                            fontWeight: notification.is_read ? 'normal' : 'bold'
                                        }}
                                    >
                                        {notification.title}
                                    </Typography>
                                    {!notification.is_read && (
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: 'primary.main',
                                                ml: 1
                                            }}
                                        />
                                    )}
                                </Box>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mb: 1,
                                        width: '100%',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {notification.message}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ alignSelf: 'flex-end' }}
                                >
                                    {formatTime(notification.created_at)}
                                </Typography>
                            </MenuItem>
                        ))
                    )}
                </Box>

                {/* Footer: always visible, link to full notifications page */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0, textAlign: 'center' }}>
                    <TertiaryButton
                        size="small"
                        onClick={() => {
                            navigate('/notifications');
                            handleClose();
                        }}
                    >
                        View all notifications
                    </TertiaryButton>
                </Box>
            </Menu>
        </>
    );
};

export default NotificationBell; 